import "./index.scss";
import "../../App.scss";
import { useEffect, useRef, useState } from "react";
import useReload from "../../Hooks/useReload";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectGoldStar } from "../../Store/store";
import { store } from "../../Store";
import {
  redirectFromParams,
  getSearchParams,
  capEveryWord,
} from "../nonUIFuncs";
import IndivGame from "../UI/GameTypeButton";
import PropTypes from "prop-types";
import { debug } from "../../utils/debug";
import {
  findSolveItNumbers,
  solveItRowsFor,
} from "../SolveIt/solveItNumbers";

/**
 * Game type selection menu component that displays available games for a specific unit.
 * Shows game cards with star ratings and allows players to select which game to play.
 * Calculates and displays progress based on player scores across different difficulty levels.
 *
 * @param {Object} props - Component props
 * @param {Array} props.scoreData - Array of player score data for calculating game progress
 * @param {Object} props.config - Application configuration object
 * @returns {JSX.Element} Game type menu with selectable game cards
 */
const GameTypeMenu = ({ scoreData, config, stories }) => {
  const [reload, reloading] = useReload();
  const goldStar = useSelector(selectGoldStar);
  const gameArrRef = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const params = getSearchParams(location);
  const unitParam = params.find((d) => d.id === "unit");
  const gnParam = params.find((d) => d.id === "game-name");
  const difficultyParam = params.find((d) => d.id === "difficulty");
  const [gameArray, setGameArray] = useState([]);
  gameArrRef.current = gameArray;
  useEffect(() => {
    let string = `${unitParam.value} games`;
    document.title = capEveryWord(string);
    let redirectPath =
      redirectFromParams(["difficulty", "unit", "game-name"]) || "";
    if (redirectPath.length > 0) {
      navigate(redirectPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (scoreData.length > 0) getGames();
  }, [scoreData]);
  /**
   * Solve It breaks without a card for every number 0-20, so it's only listed
   * when this unit's data has them all (the problems are logged in dev).
   */
  const hasSolveItNumbers = () => {
    const locationParam = params.find((d) => d.id === "location")?.value;
    const { numbers, problems } = findSolveItNumbers(
      solveItRowsFor(
        store.getState().store.value,
        gnParam.value,
        locationParam,
        config.dialectStructure,
      ),
    );
    if (!numbers) {
      debug.warn(`Solve It hidden in "${gnParam.value}":`, problems);
    }
    return Boolean(numbers);
  };
  /**
   * Processes score data to create game objects with progress information.
   * Filters games by unit and difficulty, calculates star ratings, and builds the game array.
   */
  const getGames = () => {
    let uniqueGameNames = gameArrRef.current.map((d) => d.name);
    let foundUnitGames = store.getState().store.value.gameData.filter((d) => {
      return (
        d.category === unitParam.value &&
        d.target.toString() === gnParam.value.toString()
      );
    });
    let filteredScoreData = scoreData.filter(
      (d) =>
        d.category === unitParam.value &&
        d.target.toString() === gnParam.value.toString() &&
        d.difficultyScores.easy.difficulty <= Number(difficultyParam.value),
    );
    filteredScoreData = filteredScoreData.map((d) => {
      return {
        ...d,
        categoryLanguage: foundUnitGames.categoryLanguage,
        difficulty: foundUnitGames[d.gameType],
      };
    });
    filteredScoreData.forEach((d) => {
      if (!uniqueGameNames.includes(d.gameType)) {
        uniqueGameNames.push(d.gameType);
        let gameImg = d.gameType.replaceAll("-", "_").replaceAll(" ", "_");
        let current = gameArrRef.current;
        let currentStars = 0;
        let totalStars = 0;
        if (d.difficulty <= Number(difficultyParam.value)) {
          let shortGameType = d.gameType
            .replaceAll(" ", "")
            .replaceAll("-", "");
          let gameScoringData = goldStar.find(
            (indivGS) => indivGS.game === shortGameType,
          );
          currentStars = currentStars + d.difficultyScores.easy.stars;
          if (gameScoringData?.secondType !== "none") {
            totalStars = totalStars + 6;
          } else {
            totalStars = totalStars + 3;
          }
          if (
            d.difficultyScores.medium.difficulty <=
            Number(difficultyParam.value)
          ) {
            currentStars =
              currentStars +
              d.difficultyScores.medium.firstStars +
              d.difficultyScores.medium.secondStars;
            if (gameScoringData?.secondType !== "none") {
              totalStars = totalStars + 6;
            } else {
              totalStars = totalStars + 3;
            }
          }
          if (
            d.difficultyScores.hard.difficulty <= Number(difficultyParam.value)
          ) {
            currentStars =
              currentStars +
              d.difficultyScores.hard.firstStars +
              d.difficultyScores.hard.secondStars;
            if (gameScoringData?.secondType !== "none") {
              totalStars = totalStars + 6;
            } else {
              totalStars = totalStars + 3;
            }
          }
        }
        let progress = Math.round((currentStars / totalStars) * 100);
        let starProgress = 0;
        if (progress >= 99) {
          starProgress = 3;
        } else if (progress >= 50 && progress < 99) {
          starProgress = 2;
        } else if (progress >= 1 && progress < 50) {
          starProgress = 1;
        } else {
          starProgress = 0;
        }
        if (config.PossibleGames.includes(d.gameType)) {
          if (d.gameType === "Solve It" && !hasSolveItNumbers()) {
            // Not offered: see hasSolveItNumbers
          } else if (
            d.gameType !== "Counting" ||
            (d.gameType === "Counting" &&
              store.getState().store.value.numberData.length > 0)
          ) {
            current.push({
              Key: d.Key,
              name: d.gameType,
              progress: `${progress}%`,
              stars: starProgress,
              image: `Game_Type_${gameImg}2x.png`,
            });
          } else {
            debug.warn("Game configuration error - missing image:", d);
          }
        } else {
          debug.warn(
            `Game type "${d.gameType}" does not exist in config.PossibleGames`,
          );
        }
        setGameArray(current);
        reload();
      }
    });
  };
  return (
    <div className="gamemenu_container">
      <div className="gamemenu_contents">
        {gameArrRef.current.map((obj) => {
          if (!reloading) {
            return (
              <IndivGame
                setKey={true}
                game={obj}
                path="/start-game"
                onmouseover={() => {}}
                key={obj.name}
                name={obj.name}
                progress={obj.progress}
                stars={obj.stars}
                image={obj.image}
                paramToSet="game-type"
                classNamePre="indivgame"
              />
            );
          } else {
            return null;
          }
        })}
      </div>
    </div>
  );
};

export default GameTypeMenu;

GameTypeMenu.propTypes = {
  scoreData: PropTypes.array,
  config: PropTypes.object,
  stories: PropTypes.bool,
};
